import { usePowerApps } from '@/context/PowerApps';
import { usePowerAppsDatalayer } from '@/context/PowerApps/hooks/usePowerAppsDataLayer';
import { usePowerAppsRefreshParent } from '@/context/PowerApps/hooks/usePowerAppsRefreshParent';
import { usePowerAppsTokenRefresh } from '@/context/PowerApps/hooks/usePowerAppsTokenRefresh';
import usePowerAppsBrowserDetection from '@/context/PowerApps/usePowerAppsBrowserDetection';
import { MuiBox, MuiCircularProgress } from '@/features/shared/material-ui';
import { usePostMessageListener } from '@/hooks/usePostMessageListener';
import { POWERAPPS_BASE_URL } from '@/utils/constants';
import { getGenetecPostMessage } from '@/utils/helpers';
import clsx from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResizableContainer } from './components';
import { cleanEndSlash } from './hooks/usePowerAppsDeepNavigation';
import useStyles from './PowerApps.styles';
import { usePowerAppsHeight } from './usePowerAppsHeight';
import { usePowerAppsPathListener } from './usePowerAppsPathListener';
import { usePowerAppsUrl } from './usePowerAppsUrl';

interface PowerAppsContainerProps {
  srcUrl: string;
  title: string;
  showResizeButton: boolean;
}

const experlogixPostMessageConnection = getGenetecPostMessage({
  source: 'experlogix',
  type: 'connection',
});
const genetecPostMessageConnection = getGenetecPostMessage({
  source: 'genetec',
  type: 'connection',
});

const PowerApps = ({ srcUrl, title, showResizeButton = false }: PowerAppsContainerProps) => {
  const classes = useStyles();
  const {
    powerAppsRefreshSession,
    isPowerAppsSessionExpired,
    isExperlogixConnected,
    isExperlogixConnecting,
    retryExperlogixWithPopup,
    isExperlogixSessionExpired,
    persistedQueryParams,
  } = usePowerApps();
  const lastSrcUrl = useRef('');
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeReloadMaxRetries = useRef(0);
  const powerAppsHeight = usePowerAppsHeight();

  const iframeHeight = useMemo(
    () => (isIframeLoaded && !isIframeLoading ? powerAppsHeight : 0),
    [isIframeLoaded, powerAppsHeight, isIframeLoading],
  );
  const showLoader = useMemo(() => iframeHeight <= 0, [iframeHeight]);

  usePowerAppsBrowserDetection();
  usePowerAppsDatalayer();
  usePowerAppsRefreshParent();
  usePowerAppsTokenRefresh();

  useEffect(() => {
    if (lastSrcUrl.current !== srcUrl) {
      lastSrcUrl.current = srcUrl;
      setIsIframeLoaded(false);
    }
  }, [srcUrl]);

  const handleOnLoad = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, 100));

    setIsIframeLoaded(true);
    setIsIframeLoading(false);
    powerAppsRefreshSession();
  }, [powerAppsRefreshSession]);

  const handleRequestAuthorizationMessage = useCallback(
    async (props: any) => {
      const data = experlogixPostMessageConnection(props);

      if (!data) return;

      if (!/requestAuthorization/.test(data?.event)) return;

      if (isExperlogixConnecting) return;

      if (isExperlogixSessionExpired || !isExperlogixConnected) {
        await retryExperlogixWithPopup();
      }

      iframeRef.current?.contentWindow?.postMessage('genAuthorized', '*');
    },
    [
      retryExperlogixWithPopup,
      isExperlogixConnected,
      isExperlogixConnecting,
      isExperlogixSessionExpired,
    ],
  );

  // Reload iframe on PowerApps or Experlogix session expiration
  useEffect(() => {
    if (!isIframeLoaded) return;

    if (isPowerAppsSessionExpired) {
      setIsIframeLoading(true);

      const refresh = async () => {
        if (!iframeRef.current) return;

        iframeRef.current.src = '';
        await new Promise((resolve) => setTimeout(resolve, 0));
        iframeRef.current.src = srcUrl;
      };

      refresh();
    }
  }, [isIframeLoaded, isPowerAppsSessionExpired, srcUrl]);

  usePostMessageListener(handleRequestAuthorizationMessage);

  const handleMismatchSrcUrl = React.useCallback(
    async (props: any) => {
      const data = genetecPostMessageConnection(props);
      if (!data) return;
      if (!/loaded/.test(data?.event)) return;
      if (iframeReloadMaxRetries.current > 0) return;

      try {
        const baseUrl = cleanEndSlash(POWERAPPS_BASE_URL) || '';
        const iframeUrl = cleanEndSlash(data?.data?.href) || '';

        if (iframeUrl?.startsWith(baseUrl)) return;
        if (!iframeRef.current) return;

        // It will reload the iframe, even across domains!. https://stackoverflow.com/a/4062084/824796
        iframeRef.current.src = srcUrl;
        iframeReloadMaxRetries.current += 1;
      } catch (error) {}
    },
    [srcUrl],
  );

  usePostMessageListener(handleMismatchSrcUrl);
  usePowerAppsPathListener(iframeRef);

  return (
    <div className={clsx(classes.root, 'power-apps')}>
      {showLoader && (
        <MuiBox display="flex" justifyContent="center">
          <MuiCircularProgress color="secondary" />
        </MuiBox>
      )}
      <ResizableContainer showResizeButton={showResizeButton}>
        <iframe
          title={title}
          ref={iframeRef}
          onLoad={handleOnLoad}
          style={{
            width: '100%',
            height: `${iframeHeight}px`,
            border: 'none',
          }}
          src={srcUrl}
          scrolling="no"
        />
      </ResizableContainer>
    </div>
  );
};

const WithPowerAppsUrl = ({ srcUrl, ...props }: PowerAppsContainerProps) => {
  const { srcUrl: powerAppUrl } = usePowerAppsUrl(srcUrl);

  return <PowerApps key={powerAppUrl} srcUrl={powerAppUrl} {...props} />;
};

export default memo(WithPowerAppsUrl);
